import React, { useState, useEffect, useRef, useCallback, useLayoutEffect } from 'react';
import { createPortal } from 'react-dom';
import axios from 'axios';
import { Input } from '../ui/input';
import { Label } from '../ui/label';
import { Loader2, MapPin } from 'lucide-react';

/**
 * Reusable address autosuggest:
 *   - Country: typeahead against /api/geo/countries (cached after first load).
 *   - State: typeahead within selected country.
 *   - City: typeahead within selected state (or country if state unset).
 *
 * Emits `onChange({ address_line, city, state, country, country_code, state_id, city_id, postal_code })`.
 */
export function AddressFieldsAutosuggest({ value, onChange, required = true, theme = 'cyan' }) {
  const API_URL = process.env.REACT_APP_BACKEND_URL;
  const v = value || {};

  // Country list cached on mount
  const [countries, setCountries] = useState([]);
  useEffect(() => {
    axios.get(`${API_URL}/api/geo/countries`).then(({ data }) => {
      setCountries(data.countries || []);
    }).catch(() => {});
  }, [API_URL]);

  const setField = (patch) => onChange({ ...v, ...patch });

  return (
    <div className="space-y-4">
      <div className="flex items-center gap-2 text-[#A0B4CC] text-sm">
        <MapPin className="w-4 h-4 text-[#00E5FF]" />
        <span>Your address (used for geolocation services)</span>
      </div>

      <div className="space-y-2">
        <Label className="text-white">Street Address {required && <span className="text-[#FF1744]">*</span>}</Label>
        <Input
          name="address_line"
          placeholder="123 Rizal Street, Brgy. San Antonio"
          value={v.address_line || ''}
          onChange={(e) => setField({ address_line: e.target.value })}
          className="bg-[#000B18] border-white/20 text-white placeholder:text-[#5C7599] focus:border-[#00E5FF]"
          data-testid="address-line-input"
          required={required}
        />
      </div>

      <div className="grid grid-cols-2 gap-3">
        {/* Country */}
        <CountrySelect
          countries={countries}
          value={{ name: v.country, code: v.country_code }}
          onChange={(c) => setField({
            country: c.name,
            country_code: c.iso2,
            // Reset downstream selections when country changes
            state: '',
            state_id: null,
            city: '',
            city_id: null,
          })}
          required={required}
        />
        {/* Postal code */}
        <div className="space-y-2">
          <Label className="text-white">Postal / ZIP</Label>
          <Input
            name="postal_code"
            placeholder="1000"
            value={v.postal_code || ''}
            onChange={(e) => setField({ postal_code: e.target.value })}
            className="bg-[#000B18] border-white/20 text-white placeholder:text-[#5C7599] focus:border-[#00E5FF]"
            data-testid="postal-code-input"
          />
        </div>
      </div>

      <div className="grid grid-cols-2 gap-3">
        <StateSelect
          countryCode={v.country_code}
          value={{ name: v.state, id: v.state_id }}
          onChange={(s) => setField({
            state: s.name,
            state_id: s.id,
            city: '',
            city_id: null,
          })}
          required={required}
        />
        <CitySelect
          countryCode={v.country_code}
          stateId={v.state_id}
          value={{ name: v.city, id: v.city_id }}
          onChange={(c) => setField({
            city: c.name,
            city_id: c.id,
          })}
          required={required}
        />
      </div>
    </div>
  );
}

// ============== AUTOSUGGEST FIELD ==============
function AutosuggestField({
  label,
  placeholder,
  value,
  onChange,
  onSelect,
  fetcher,
  testId,
  disabled,
  required,
  renderItem,
  itemKey,
  itemLabel,
}) {
  const [results, setResults] = useState([]);
  const [open, setOpen] = useState(false);
  const [loading, setLoading] = useState(false);
  const [highlight, setHighlight] = useState(0);
  const inputRef = useRef(null);
  const containerRef = useRef(null);
  // Portal-based positioning so the dropdown can escape modal / table /
  // overflow-hidden containers. When the visual viewport is short (mobile
  // keyboard open) we anchor the menu's BOTTOM to the input top instead of
  // computing a static top offset — otherwise the menu would float far above
  // the input because we don't know its real rendered height in advance.
  const [coords, setCoords] = useState({ top: undefined, bottom: undefined, left: 0, width: 0, openUp: false });
  const recomputeCoords = useCallback(() => {
    const el = inputRef.current;
    if (!el) return;
    const r = el.getBoundingClientRect();
    // Prefer visualViewport when available so mobile keyboards are accounted for.
    const vv = window.visualViewport;
    const winH = vv?.height ?? window.innerHeight;
    const spaceBelow = winH - r.bottom;
    const spaceAbove = r.top;
    // Only flip up when we truly don't have room below AND we have more room
    // above. Use a small threshold (120 px) so a single suggestion doesn't
    // force a flip.
    const openUp = spaceBelow < 140 && spaceAbove > spaceBelow;
    setCoords({
      top: openUp ? undefined : r.bottom + 4,
      // Anchor the dropdown BOTTOM 4 px above the input top so it always sits
      // flush against the field, regardless of how many suggestions it holds.
      bottom: openUp ? Math.max(winH - r.top + 4, 4) : undefined,
      left: r.left,
      width: r.width,
      openUp,
    });
  }, []);
  useLayoutEffect(() => {
    if (open) recomputeCoords();
  }, [open, results.length, recomputeCoords]);
  useEffect(() => {
    if (!open) return undefined;
    const handler = () => recomputeCoords();
    window.addEventListener('scroll', handler, true);
    window.addEventListener('resize', handler);
    // visualViewport tracks the on-screen keyboard on mobile; without this,
    // the dropdown stays where it was placed BEFORE the keyboard appeared.
    window.visualViewport?.addEventListener('resize', handler);
    window.visualViewport?.addEventListener('scroll', handler);
    return () => {
      window.removeEventListener('scroll', handler, true);
      window.removeEventListener('resize', handler);
      window.visualViewport?.removeEventListener('resize', handler);
      window.visualViewport?.removeEventListener('scroll', handler);
    };
  }, [open, recomputeCoords]);

  const debouncedFetch = useDebouncedCallback(async (q) => {
    if (disabled) return;
    setLoading(true);
    try {
      const items = await fetcher(q);
      setResults(items || []);
      setOpen(true);
      setHighlight(0);
    } catch { setResults([]); }
    setLoading(false);
  }, 200);

  useEffect(() => {
    const onDoc = (e) => {
      if (containerRef.current && !containerRef.current.contains(e.target)) setOpen(false);
    };
    document.addEventListener('mousedown', onDoc);
    return () => document.removeEventListener('mousedown', onDoc);
  }, []);

  return (
    <div className="space-y-2 relative" ref={containerRef}>
      <Label className="text-white">{label}{required && <span className="text-[#FF1744]"> *</span>}</Label>
      <div className="relative">
        <Input
          ref={inputRef}
          placeholder={placeholder}
          value={value || ''}
          disabled={disabled}
          onChange={(e) => {
            onChange(e.target.value);
            debouncedFetch(e.target.value);
          }}
          onFocus={() => {
            if (!value) debouncedFetch('');
            else if (results.length) setOpen(true);
          }}
          onKeyDown={(e) => {
            if (!open) return;
            if (e.key === 'ArrowDown') { e.preventDefault(); setHighlight((h) => Math.min(h + 1, results.length - 1)); }
            else if (e.key === 'ArrowUp') { e.preventDefault(); setHighlight((h) => Math.max(h - 1, 0)); }
            else if (e.key === 'Enter' && results[highlight]) { e.preventDefault(); onSelect(results[highlight]); setOpen(false); }
            else if (e.key === 'Escape') setOpen(false);
          }}
          className="bg-[#000B18] border-white/20 text-white placeholder:text-[#5C7599] focus:border-[#00E5FF] pr-9"
          data-testid={testId}
          autoComplete="off"
          required={required}
        />
        {loading && (
          <Loader2 className="w-4 h-4 animate-spin text-[#5C7599] absolute right-3 top-1/2 -translate-y-1/2" />
        )}
      </div>
      {open && results.length > 0 && createPortal(
        <ul
          style={{ position: 'fixed', top: coords.top, bottom: coords.bottom, left: coords.left, width: coords.width, zIndex: 9999 }}
          className="max-h-56 overflow-y-auto rounded-md border border-white/10 bg-[#041A3B] shadow-xl"
          data-testid={`${testId}-dropdown`}
        >
          {results.slice(0, 30).map((item, idx) => (
            <li
              key={itemKey(item)}
              // Use onMouseDown with preventDefault to avoid input blur swallowing the click
              // (otherwise Radix Dialog focus traps + document mousedown can close the dropdown
              // before the onClick fires).
              onMouseDown={(e) => {
                e.preventDefault();
                e.stopPropagation();
                onSelect(item);
                setOpen(false);
              }}
              onMouseEnter={() => setHighlight(idx)}
              className={`px-3 py-2 text-sm cursor-pointer ${
                highlight === idx ? 'bg-[#00E5FF]/15 text-[#00E5FF]' : 'text-[#A0B4CC] hover:bg-white/5'
              }`}
              data-testid={`${testId}-option-${idx}`}
            >
              {renderItem ? renderItem(item) : itemLabel(item)}
            </li>
          ))}
        </ul>,
        document.body,
      )}
    </div>
  );
}

// ============== Country / State / City wrappers ==============
function CountrySelect({ countries, value, onChange, required }) {
  return (
    <AutosuggestField
      label="Country"
      placeholder="Start typing..."
      value={value?.name}
      onChange={(text) => onChange({ name: text, iso2: '' })}
      onSelect={(c) => onChange(c)}
      fetcher={(q) => {
        const ql = (q || '').toLowerCase();
        const list = countries.filter((c) => c.name.toLowerCase().includes(ql));
        return list.slice(0, 50);
      }}
      testId="country-input"
      required={required}
      itemKey={(c) => c.iso2}
      itemLabel={(c) => `${c.emoji || ''} ${c.name}`}
      renderItem={(c) => (
        <span className="flex items-center gap-2">
          <span>{c.emoji || ''}</span>
          <span>{c.name}</span>
          <span className="text-xs text-[#5C7599] ml-auto">{c.iso2}</span>
        </span>
      )}
    />
  );
}

function StateSelect({ countryCode, value, onChange, required }) {
  const API_URL = process.env.REACT_APP_BACKEND_URL;
  // Preload ALL states for the chosen country once. Most countries have <300
  // states so this is a single ~10 KB fetch — eliminates the perceived "stuck"
  // experience of debounced per-keystroke requests and lets us substring-match
  // client-side instead of being limited to backend prefix matching.
  const [allStates, setAllStates] = useState([]);
  useEffect(() => {
    if (!countryCode) { setAllStates([]); return; }
    let cancelled = false;
    axios
      .get(`${API_URL}/api/geo/states?country=${countryCode}&limit=500`)
      .then(({ data }) => { if (!cancelled) setAllStates(data.states || []); })
      .catch(() => { if (!cancelled) setAllStates([]); });
    return () => { cancelled = true; };
  }, [API_URL, countryCode]);

  return (
    <AutosuggestField
      label="State / Province"
      placeholder={countryCode ? 'Start typing...' : 'Select country first'}
      value={value?.name}
      disabled={!countryCode}
      onChange={(text) => onChange({ name: text, id: null })}
      onSelect={(s) => onChange(s)}
      fetcher={(q) => {
        const ql = (q || '').toLowerCase();
        if (!ql) return allStates.slice(0, 100);
        return allStates.filter(s => (s.name || '').toLowerCase().includes(ql)).slice(0, 100);
      }}
      testId="state-input"
      required={required}
      itemKey={(s) => s.id}
      itemLabel={(s) => s.name}
    />
  );
}

function CitySelect({ countryCode, stateId, value, onChange, required }) {
  const API_URL = process.env.REACT_APP_BACKEND_URL;
  // Preload top cities for the (country, state) pair once — substring match
  // client-side. Cities can be very numerous so we cap at 1000; if the user's
  // city is rarer we still fall back to a server query inside the fetcher.
  const [cityCache, setCityCache] = useState([]);
  useEffect(() => {
    if (!countryCode) { setCityCache([]); return; }
    let cancelled = false;
    const params = new URLSearchParams({ country: countryCode, limit: '1000' });
    if (stateId) params.set('state_id', String(stateId));
    axios
      .get(`${API_URL}/api/geo/cities?${params.toString()}`)
      .then(({ data }) => { if (!cancelled) setCityCache(data.cities || []); })
      .catch(() => { if (!cancelled) setCityCache([]); });
    return () => { cancelled = true; };
  }, [API_URL, countryCode, stateId]);

  return (
    <AutosuggestField
      label="City"
      placeholder={countryCode ? 'Start typing...' : 'Select country first'}
      value={value?.name}
      disabled={!countryCode}
      onChange={(text) => onChange({ name: text, id: null })}
      onSelect={(c) => onChange(c)}
      fetcher={async (q) => {
        const ql = (q || '').toLowerCase();
        if (!ql) return cityCache.slice(0, 100);
        const local = cityCache.filter(c => (c.name || '').toLowerCase().includes(ql)).slice(0, 100);
        // If the cache covers the typed query well, skip the network hop.
        if (local.length >= 5) return local;
        // Fallback: server-side prefix match for rare cities not in top 1000.
        try {
          const params = new URLSearchParams({ country: countryCode, limit: '50', q });
          if (stateId) params.set('state_id', String(stateId));
          const { data } = await axios.get(`${API_URL}/api/geo/cities?${params.toString()}`);
          const merged = [...local];
          (data.cities || []).forEach(c => {
            if (!merged.find(m => m.id === c.id)) merged.push(c);
          });
          return merged.slice(0, 100);
        } catch {
          return local;
        }
      }}
      testId="city-input"
      required={required}
      itemKey={(c) => c.id}
      itemLabel={(c) => `${c.name}${c.state_name ? ` — ${c.state_name}` : ''}`}
    />
  );
}

// Lightweight debounced callback (200 ms)
function useDebouncedCallback(fn, wait = 200) {
  const timeoutRef = useRef(null);
  const fnRef = useRef(fn);
  useEffect(() => { fnRef.current = fn; }, [fn]);
  return useCallback((...args) => {
    if (timeoutRef.current) clearTimeout(timeoutRef.current);
    timeoutRef.current = setTimeout(() => fnRef.current(...args), wait);
  }, [wait]);
}
